import { useState, useEffect, useMemo } from "react";
import { ChevronLeft, ChevronRight, ChevronDown } from "lucide-react";
import { cn } from "@/lib/utils";
import {
  Carousel,
  CarouselContent,
  CarouselItem,
  type CarouselApi,
} from "@/components/ui/carousel";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { mapOfferToDestination } from "@/adapters/offerAdapter";
import OfferCard from "@/components/offers/OfferCard";
import OfferCardSkeleton from "@/components/offers/OfferCardSkeleton";
import { Offer } from "@/types/offer";
import { getOffers, getOrgins } from "@/services/offers.service";
import { Ubigeo } from "@/types/ubigeo";

const SKELETON_COUNT = 4;
const FALLBACK_LIMA: Ubigeo = {
  code: "LIM",
  description: "Lima"
};

const DestinationsSection = () => {
  const [api, setApi] = useState<CarouselApi>();
  const [canScrollPrev, setCanScrollPrev] = useState(false);
  const [canScrollNext, setCanScrollNext] = useState(false);
  const [origins, setOrigins] = useState<Ubigeo[]>([]);
  const [selectedCity, setSelectedCity] = useState<Ubigeo | null>(null);
  
  const [offers, setOffers] = useState<Offer[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [error, setError] = useState<boolean>(false);
  
  const params = {
    destinations: [
      "MAD", "MIA","BCN", "PAR", "EZE", "ROM",
      "CUZ", "AQP", "TRU"
    ].toString(),
    adults: "1",
    excludeChildren: true,
  };
  const destinations = useMemo(() => {
    return offers?.map(mapOfferToDestination) ?? [];
  }, [offers]);

  useEffect(() => {
    const fetchOrigins = async () => {
      try {
        const data = await getOrgins(params);

        setOrigins(data);

        const lima = data.find(o => o.code === "LIM");

        setSelectedCity(lima ?? FALLBACK_LIMA);
      } catch (e) {
        setSelectedCity(FALLBACK_LIMA);
      }
    };

    fetchOrigins();
  }, []);

  useEffect(() => {
    if (!selectedCity?.code) return;

    let isMounted = true;

    const fetchOffers = async () => {
      try {
        setIsLoading(true);
        
        const data = await getOffers({
          origins: selectedCity.code,
          limit: 12,
          ...params
        });

        const domestic = data.filter(o => o.flightType === "DOMESTIC");
        const international = data.filter(o => o.flightType === "INTERNATIONAL");
        const interleavedOffers: typeof data = [];
        const maxLength = Math.max(domestic.length, international.length);

        for (let i = 0; i < maxLength; i++) {
          if (domestic[i]) interleavedOffers.push(domestic[i]);
          if (international[i]) interleavedOffers.push(international[i]);
        }

        if (isMounted) {
          setOffers(interleavedOffers);
        }
      } catch (error) {
        setError(true);
      } finally {
        if (isMounted) {
          setIsLoading(false);
        }
      }
    };

    fetchOffers();

    return () => {
      isMounted = false;
    };
  }, [selectedCity]);

  useEffect(() => {
    if (!api) return;

    const updateScrollState = () => {
      setCanScrollPrev(api.canScrollPrev());
      setCanScrollNext(api.canScrollNext());
    };

    updateScrollState();
    api.on("select", updateScrollState);
    api.on("reInit", updateScrollState);

    return () => {
      api.off("select", updateScrollState);
    };
  }, [api]);

  if (error) {
    return null;
  }

  return (
    <section className="py-16 bg-background">
      <div className="container mx-auto px-4">
        {/* Header */}
        <div className="mb-8">
          <h2 className="text-3xl lg:text-4xl font-light text-foreground italic mb-2">
            Descubre tu próximo viaje
          </h2>
          <div className="flex items-center gap-2 text-muted-foreground">
            <span>Ofertas de vuelo desde</span>
            <DropdownMenu>
              <DropdownMenuTrigger className="inline-flex items-center gap-1 font-semibold text-primary hover:text-primary/80 transition-colors focus:outline-none">
                {selectedCity?.description ?? FALLBACK_LIMA.description}
                <ChevronDown className="w-4 h-4" />
              </DropdownMenuTrigger>
              <DropdownMenuContent 
                align="start" 
                className="max-h-64 overflow-y-auto bg-background border border-border shadow-lg z-50"
              >
                {origins.map((city) => (
                  <DropdownMenuItem
                    key={city.code}
                    onClick={() => setSelectedCity(city)}
                    className={cn(
                      "cursor-pointer",
                      selectedCity.code === city.code && "bg-primary/10 text-primary"
                    )}
                  >
                    {city.description}
                  </DropdownMenuItem>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>

        {/* Peeking Carousel */}
        <div className="relative group/carousel">
          {/* Overlay Navigation Arrows */}
          <button
            onClick={() => api?.scrollPrev()}
            disabled={!canScrollPrev}
            className={cn(
              "absolute -left-4 md:-left-6 top-1/2 -translate-y-1/2 z-20 w-12 h-12 rounded-full flex items-center justify-center transition-all duration-300 shadow-xl bg-primary",
              canScrollPrev
                ? "hover:opacity-90 hover:scale-105 cursor-pointer"
                : "opacity-40 cursor-not-allowed"
            )}
            aria-label="Anterior"
          >
            <ChevronLeft className="w-6 h-6 text-white" />
          </button>
          <button
            onClick={() => api?.scrollNext()}
            disabled={!canScrollNext}
            className={cn(
              "absolute -right-4 md:-right-6 top-1/2 -translate-y-1/2 z-20 w-12 h-12 rounded-full flex items-center justify-center transition-all duration-300 shadow-xl bg-primary",
              canScrollNext
                ? "hover:opacity-90 hover:scale-105 cursor-pointer"
                : "opacity-40 cursor-not-allowed"
            )}
            aria-label="Siguiente"
          >
            <ChevronRight className="w-6 h-6 text-white" />
          </button>

          <Carousel
            setApi={setApi}
            opts={{
              align: "start",
              loop: false,
            }}
            className="w-full"
          >
            <CarouselContent className="-ml-4">
              {isLoading
                ? Array.from({ length: SKELETON_COUNT }).map((_, index) => (
                    <CarouselItem
                      key={`skeleton-${index}`}
                      className="pl-4 basis-[85%] sm:basis-[45%] lg:basis-[30%]"
                    >
                      <OfferCardSkeleton />
                    </CarouselItem>
                  ))
                : destinations.map((destination, index) => (
                    <CarouselItem
                      key={destination.id}
                      className="pl-4 basis-[85%] sm:basis-[45%] lg:basis-[30%]"
                    >
                      <OfferCard destination={destination} index={index} />
                    </CarouselItem>
                  ))}
            </CarouselContent>
          </Carousel>
          {error && <p className="text-red-500">{error}</p>}
        </div>
      </div>
    </section>
  );
};

export default DestinationsSection;
